import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiExternalLink, FiGithub, FiCalendar, FiTag } from 'react-icons/fi';
import Badge from '../../UI/Badge';
import { LoginPageDemo, PasswordGeneratorDemo, WordlistGeneratorDemo, LandingPageDemo } from '../demos';
import './ProjectModal.css';

const ProjectModal = ({ project, onClose }) => {
  const {
    title,
    longDescription,
    category,
    technologies,
    status,
    year,
    demoUrl,
    githubUrl,
    features,
    icon
  } = project;

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [onClose]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'completed':
        return 'Concluído';
      case 'in-progress':
        return 'Em desenvolvimento';
      default:
        return 'Planejado';
    }
  };

  const getStatusVariant = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in-progress':
        return 'warning';
      default:
        return 'secondary';
    }
  };

  const modalVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 }
  };

  const contentVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { duration: 0.3 }
    },
    exit: { 
      opacity: 0, 
      scale: 0.8,
      transition: { duration: 0.2 }
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        className="project-modal"
        variants={modalVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        onClick={handleBackdropClick}
      >
        <motion.div
          className="project-modal__content"
          variants={contentVariants}
        >
          <button
            className="project-modal__close"
            onClick={onClose}
            aria-label="Fechar modal"
          >
            <FiX />
          </button>

          <div className="project-modal__header">
            <div className="project-modal__image">
              {project.image ? (
                <img src={project.image} alt={title} />
              ) : (
                <div className="project-modal__image-placeholder">
                  {icon || '📁'}
                  <span>Preview em breve</span>
                </div>
              )}
            </div>

            <div className="project-modal__info">
              <h2 className="project-modal__title">{title}</h2>
              
              <div className="project-modal__meta">
                <div className="project-modal__category">
                  <FiTag />
                  <Badge variant="primary">{category}</Badge>
                </div>
                <div className="project-modal__year">
                  <FiCalendar />
                  <span>{year}</span>
                </div>
              </div>

              <p className="project-modal__description">
                {longDescription || project.description}
              </p>

              <div className="project-modal__actions">
                {githubUrl && (
                  <a
                    href={githubUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="project-modal__action"
                  >
                    <FiGithub />
                    Ver Código
                  </a>
                )}
                {demoUrl && (
                  <a
                    href={demoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="project-modal__action project-modal__action--primary"
                  >
                    <FiExternalLink />
                    Ver Projeto
                  </a>
                )}
              </div>
            </div>
          </div>

          <div className="project-modal__details">
            <div className="project-modal__section">
              <h3>Tecnologias Utilizadas</h3>
              <div className="project-modal__technologies">
                {technologies.map((tech, index) => (
                  <span key={index} className="project-modal__tech">
                    {tech}
                  </span>
                ))}
              </div>
            </div>

            {features && features.length > 0 && (
              <div className="project-modal__section">
                <h3>Principais Funcionalidades</h3>
                <ul className="project-modal__features">
                  {features.map((feature, index) => (
                    <li key={index}>{feature}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="project-modal__section">
              <h3>Status do Projeto</h3>
              <Badge
                variant={getStatusVariant(status)}
                size="medium"
              >
                {getStatusLabel(status)}
              </Badge>
            </div>

            {/* Demonstração interativa para o projeto Login Page */}
            {title === 'Login Page' && (
              <div className="project-modal__section">
                <LoginPageDemo />
              </div>
            )}

            {/* Demonstração interativa para o projeto Password Generator */}
            {title === 'Password Generator' && (
              <div className="project-modal__section">
                <PasswordGeneratorDemo />
              </div>
            )}

            {/* Demonstração interativa para o projeto Wordlist Generator */}
            {title === 'Wordlist Generator' && (
              <div className="project-modal__section">
                <WordlistGeneratorDemo />
              </div>
            )}

            {/* Demonstração interativa para o projeto Landing Page */}
            {title === 'Landing Page' && (
              <div className="project-modal__section">
                <LandingPageDemo />
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ProjectModal;